import { SIGNUP_PAID_LABEL, SIGNUP_PAID_TICK_COLUMNS } from "./signupSheetLayout";
import {
  printedBracketColumns,
  sheetKindFromBowlerQuantity,
  uniqueOptionalEvents,
} from "../../shared/signupSheets/printedTickColumns";

export { SIGNUP_PAID_LABEL, SIGNUP_PAID_TICK_COLUMNS };

export const isSignupPaidLabel = (value) =>
  String(value || "").trim().toLowerCase() === SIGNUP_PAID_LABEL.toLowerCase();

export const signupSheetTickColumnCount = (optionalEventCount = 0) =>
  Math.max(0, Number(optionalEventCount) || 0) + SIGNUP_PAID_TICK_COLUMNS;

export {
  applyDoublesSinglesOption,
  DOUBLES_SINGLES,
  isSinglesLikeEvent,
  optionalEventLabel,
  printedOptionalTickEvents,
  printedOptionalTickLabels,
  printSinglesEventFromEvents,
  printedBracketColumns,
  printedBracketModes,
  sheetKindFromBowlerQuantity,
  uniqueOptionalEvents,
} from "../../shared/signupSheets/printedTickColumns";

export const teamSizeForKind = (kind) => {
  if (kind === "singles") return 1;
  if (kind === "doubles") return 2;
  if (kind === "team5") return 5;
  return 4;
};

export const normalizeSignupPadEventName = (value) =>
  String(value || "")
    .split("·")[0]
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const padEventAliases = (event) => {
  const name = normalizeSignupPadEventName(event?.eventName);
  const abbreviation = normalizeSignupPadEventName(
    event?.eventAbbreviation
  );
  return [name, abbreviation].filter(Boolean);
};

const uniqueEvents = (events) => (events.length === 1 ? events[0] : null);

export const matchSignupPadEvent = (printedName, primaryEvents = []) => {
  const needle = normalizeSignupPadEventName(printedName);
  if (!needle) return null;
  const events = (primaryEvents || []).filter(Boolean);
  const exact = events.filter((event) =>
    padEventAliases(event).some((alias) => alias === needle)
  );
  const exactHit = uniqueEvents(exact);
  if (exactHit) return exactHit;

  const loose = events.filter((event) =>
    padEventAliases(event).some(
      (alias) =>
        alias === needle ||
        alias.startsWith(`${needle} `) ||
        needle.startsWith(`${alias} `)
    )
  );
  return uniqueEvents(loose);
};

export const inferSignupPadEvent = (entries, primaryEvents = []) => {
  const events = (primaryEvents || []).filter(Boolean);
  const named = (entries || []).filter(
    (entry) => (entry?.bowlers || []).length
  );
  if (!named.length || !events.length) return null;

  const sizes = named.map((entry) => (entry.bowlers || []).length);
  const maxSize = Math.max(...sizes);
  const hasTeamName = named.some((entry) =>
    Boolean(String(entry.teamName || "").trim())
  );

  if (maxSize >= 3 || hasTeamName) {
    const teams = events.filter(
      (event) => Number(event.bowlerQuantity) >= 3
    );
    const exactSize = teams.filter(
      (event) => Number(event.bowlerQuantity) === maxSize
    );
    return uniqueEvents(exactSize) || uniqueEvents(teams);
  }
  if (maxSize === 2) {
    return uniqueEvents(
      events.filter((event) => Number(event.bowlerQuantity) === 2)
    );
  }
  return uniqueEvents(
    events.filter((event) => Number(event.bowlerQuantity) <= 1)
  );
};

export const resolveSignupPadEvent = ({
  printedEvent,
  primaryEvents = [],
} = {}) => {
  const events = (primaryEvents || []).filter(Boolean);
  return matchSignupPadEvent(printedEvent, events);
};

export const packedEntriesPerPage = ({
  showLaneColumn,
  showTeamName,
  teamSize,
}) => {
  const pageHeight = 792;
  const margin = 32;
  const titleHeight = 60;
  const headerHeight = 20;
  const footerHeight = 22;
  const rowHeight = 23;
  const laneBarHeight = 18;
  const teamBarHeight = 26;
  const groupGap = showLaneColumn ? 6 : 10;
  const bar = showLaneColumn ? 0 : showTeamName ? teamBarHeight : laneBarHeight;
  const groupHeight = bar + Math.max(1, teamSize) * rowHeight + groupGap;
  const available =
    pageHeight - margin * 2 - titleHeight - headerHeight - footerHeight;
  return Math.max(1, Math.floor(available / groupHeight));
};

export const entriesPerPage = ({ kind }) => {
  const showLaneColumn = kind === "singles";
  const showTeamName = kind === "team4" || kind === "team5";
  const teamSize =
    kind === "singles" ? 1 : kind === "doubles" ? 2 : kind === "team5" ? 5 : 4;
  return packedEntriesPerPage({ showLaneColumn, showTeamName, teamSize });
};

export const defaultSignupSheetPages = ({
  maxPerSquad,
  entriesPerPage: perPage,
}) => {
  const max = Number(maxPerSquad) || 0;
  const size = Number(perPage) || 1;
  if (max <= 0) return 2;
  return Math.max(1, Math.ceil(max / size));
};

const slugPart = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

export const signupSheetFileName = ({
  tournamentName,
  eventName,
  multiple,
}) => {
  const parts = multiple
    ? [slugPart(tournamentName) || "tournament", "signups"]
    : [
        slugPart(tournamentName) || "tournament",
        slugPart(eventName) || "event",
        "signup",
      ];
  return `${parts.filter(Boolean).join("-")}.pdf`;
};

const eventSubtitle = (kind, eventName, teamSize) => {
  if (kind === "singles") return `${eventName} · 1 per entry`;
  if (kind === "doubles") return `${eventName} · 2 per pair`;
  return `${eventName} · ${teamSize} per team`;
};

export const buildSignupSheetModel = ({
  tournamentName,
  event,
  maxPerSquad,
  optionalEvents,
  pageCount,
  bracketModes,
}) => {
  const bowlerQuantity = Number(event?.bowlerQuantity) || 1;
  const kind = sheetKindFromBowlerQuantity(bowlerQuantity);
  const teamSize = bowlerQuantity;
  const eventName = event?.eventName || "Event";
  const subtitle = eventSubtitle(kind, eventName, teamSize);
  const perPage = packedEntriesPerPage({
    showLaneColumn: kind === "singles",
    showTeamName: teamSize > 2,
    teamSize,
  });
  const pages =
    Number(pageCount) > 0
      ? Number(pageCount)
      : defaultSignupSheetPages({
          maxPerSquad,
          entriesPerPage: perPage,
        });

  return {
    tournamentName: tournamentName || "Tournament",
    eventName,
    eventSubtitle: subtitle,
    kind,
    teamSize,
    showTeamName: teamSize > 2,
    showLaneColumn: kind === "singles",
    showUsbc: false,
    showEmail: false,
    showPhone: false,
    optionalEvents: uniqueOptionalEvents(optionalEvents),
    showHBrk: printedBracketColumns(bracketModes).some(
      (col) => col.scoringMode === "handicap"
    ),
    showSBrk: printedBracketColumns(bracketModes).some(
      (col) => col.scoringMode === "scratch"
    ),
    entriesPerPage: perPage,
    pageCount: pages,
  };
};
